/**
 * Teams Page
 * Lists ALL teams in the project.
 * Click a team → navigates to /teams/:teamId for full management.
 * Has a "Create Team" button.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAllTeams, createTeam } from '../../appwrite';
import { ID } from 'appwrite';
import './Teams.css';

const Teams = () => {
  const [teamsList, setTeamsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // Create team modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);

  /**
   * Fetches all teams via REST API.
   */
  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listAllTeams({ limit: 100 });
      setTeamsList(response.teams);
    } catch (err) {
      setError(err.message || 'Failed to load teams.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  /**
   * Creates a new team.
   */
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setCreatingTeam(true);
    setError('');
    try {
      await createTeam(ID.unique(), newTeamName.trim());
      setShowCreateModal(false);
      setNewTeamName('');
      setSuccessMessage('Team created successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
      await fetchTeams();
    } catch (err) {
      setError(err.message || 'Failed to create team.');
    } finally {
      setCreatingTeam(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="teams-page">
        <div className="teams-loading">
          <div className="loading-spinner"></div>
          <p>Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teams-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Teams</h1>
          <span className="page-count">{teamsList.length} total</span>
        </div>
        <button className="create-btn" onClick={() => setShowCreateModal(true)}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3V13M3 8H13" />
          </svg>
          New Team
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="members-alert members-alert-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMessage && (
        <div className="members-alert members-alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Teams grid — clicking navigates to team detail */}
      <div className="teams-grid">
        {teamsList.map(team => (
          <div
            key={team.$id}
            className="team-card"
            onClick={() => navigate(`/teams/${team.$id}`)}
          >
            <div className="team-card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="9" cy="8" r="3" />
                <circle cx="15" cy="8" r="3" />
                <path d="M3 20c0-3.5 2.5-6 6-6s6 2.5 6 6" />
                <path d="M15 14c2.5 0 4.5 1.5 4.5 4" />
              </svg>
            </div>
            <div className="team-card-info">
              <h3 className="team-card-name">{team.name}</h3>
              <div className="team-card-meta">
                <span className="team-card-members">
                  {team.total} member{team.total !== 1 ? 's' : ''}
                </span>
                <span className="team-card-dot">·</span>
                <span className="team-card-date">
                  {new Date(team.$createdAt).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric'
                  })}
                </span>
              </div>
            </div>
            <svg className="team-card-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M7 4L13 10L7 16" />
            </svg>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {teamsList.length === 0 && !loading && (
        <div className="teams-empty">
          <p>No teams found. Create one to get started.</p>
        </div>
      )}

      {/* ===== Create Team Modal ===== */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Create New Team</h2>
            <form className="modal-form" onSubmit={handleCreateTeam}>
              <div className="modal-field">
                <label>Team Name</label>
                <input
                  type="text"
                  placeholder="e.g. Engineering, Marketing"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="action-btn action-cancel" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-save" disabled={creatingTeam}>
                  {creatingTeam ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;
